import * as Dfns from 'date-fns/fp';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { SelectorOption } from '@Components/ModalEditWidget/modal-edit-constants';

export const GOAL_NAMES = [
  'Budget',
  'CPC',
  'CPM',
  'CPX',
  'CTR',
  'KPI Volume',
  'Revenue',
  'ROAS'
] as const;

// Goals whose value is a plain number (ratio or count) rather than a dollar amount.
export const DECIMAL_GOAL_NAMES: ReadonlySet<string> = new Set(['ROAS', 'CTR', 'KPI Volume']);

// Goals that pace on spend alone; every other goal also needs a KPI + KPI Source.
export const KPI_OPTIONAL_GOAL_NAMES: ReadonlySet<string> = new Set([
  'Budget',
  'CPC',
  'CTR',
  'CPM'
]);

// Segments that hold multiple values (by segment name, as returned by getBudgetCustomMapping).
export const MULTI_VALUE_SEGMENTS: ReadonlySet<string> = new Set(['Channel', 'Platform']);

// TVAD channels; a budget for them is not combined with non-TVAD channels. Display is left out
// until it is confirmed as TVAD (it is also a non-TVAD digital channel for some companies).
export const TVAD_CHANNELS: ReadonlySet<string> = new Set([
  'Streaming Video',
  'Video',
  'Streaming Audio',
  'Podcast',
  'Linear',
  'Radio'
]);

// Goal granularity per media type (Settings modal).
export const GranularityLevel = {
  CAMPAIGN: 'campaign',
  SEGMENT: 'segment'
} as const;

export const GRANULARITY_OPTIONS = [
  { label: 'Campaign and Segment Level', value: GranularityLevel.CAMPAIGN },
  { label: 'Segment Level', value: GranularityLevel.SEGMENT }
];

// Keys into GoalSelectorOptions for the fixed (non-segment) select fields. Custom segments are
// keyed by their own segment name.
export const OptionsKey = {
  GOAL_NAME: 'goalNameOptions',
  KPI: 'Kpi',
  SOURCE: 'Source',
  CAMPAIGN: 'Campaign (Platform)',
  AD_GROUP: 'Ad Group (Platform)'
} as const;

export const FIELD_TO_OPTIONS_KEY: Record<string, string> = {
  goal_name: OptionsKey.GOAL_NAME,
  kpi: OptionsKey.KPI,
  source: OptionsKey.SOURCE,
  campaign_platform: OptionsKey.CAMPAIGN,
  ad_group_platform: OptionsKey.AD_GROUP
};

export const FIELD_TO_DISPLAY_LABEL: Record<string, string> = {
  start_date: 'Start Date',
  end_date: 'End Date',
  goal_name: 'Goal Name',
  goal_value: 'Goal Value',
  kpi: 'KPI',
  source: 'KPI Source',
  campaign_platform: 'Campaign (Platform)',
  ad_group_platform: 'Ad Group (Platform)'
};

// Columns that hold multiple values, in options-key form.
export const MULTI_VALUE_COLUMN_KEYS: ReadonlySet<string> = new Set([
  ...MULTI_VALUE_SEGMENTS,
  OptionsKey.CAMPAIGN,
  OptionsKey.AD_GROUP
]);

export type GoalSelectorOptions = Record<string, SelectorOption[]>;

export interface GoalRow {
  id?: number;
  company?: string;
  start_date?: string;
  end_date?: string;
  goal_name?: string;
  goal_value?: number | null;
  kpi?: string | null;
  source?: string | null;
  campaign_platform?: string[] | null;
  ad_group_platform?: string[] | null;
  lastmodified?: string;
  lastuser?: string;
  // Row from the legacy expected_bookings table: saved through /update_expected_bookings (with
  // its row id and media), never through the Goal Intake save.
  legacy?: boolean;
  legacy_id?: number;
  legacy_media?: string;
  // Set on a legacy row when a saved Goal Intake row overlaps it (both are shown, legacy wins).
  legacyConflict?: boolean;
  // Custom segment columns, keyed by segment name (company-specific).
  [segment: string]: unknown;
}

export interface GoalsSavePayload {
  insert: GoalRow[];
  update: GoalRow[];
  delete: GoalRow[];
  company: string;
  customSegments: string[];
}

export interface MediaTypeSetting {
  mediaType: string;
  isMediaTypeActive: boolean;
}

export const HideAlertKey = {
  DOWNLOAD: 'download_hideAlert',
  BULK_UPLOAD: 'bulkUpload_hideAlert'
} as const;
export type HideAlertKeyType = (typeof HideAlertKey)[keyof typeof HideAlertKey];

export const BULK_ALERT_TITLE: Record<HideAlertKeyType, string> = {
  [HideAlertKey.DOWNLOAD]: 'Download Alert',
  [HideAlertKey.BULK_UPLOAD]: 'Bulk Upload Alert'
};

export const EMPTY_OPTIONS: GoalSelectorOptions = {};
export const NO_SEGMENTS: string[] = [];
export const ALLOW_ALL_DATES = () => false;

// Workbook layout: id / Start Date / End Date come first, so option-backed columns start at 4.
export const FIRST_OPTION_COLUMN = 4;
export const DATA_VALIDATION_ROWS = 1000;

// Legacy bookings and Channel view dates are calendar dates, not fiscal ones.
export const STANDARD_CALENDAR: CompanyFiscalCal = {};

const LEGACY_DATE_FORMAT = Dfns.format('M/d/yy');
export const formatLegacyDate = (date: string): string => LEGACY_DATE_FORMAT(Dfns.parseISO(date));

export const WEEKS_AHEAD = 26;
export const WEEKS_BACK = 52;

export const GoalView = {
  MONTH: 'Month',
  WEEK: 'Week',
  CHANNEL: 'Channel'
} as const;
export type GoalViewType = (typeof GoalView)[keyof typeof GoalView];

// A row being edited. Saved rows are keyed by their id; new rows get a temporary `new-N` key.
export interface DraftRow {
  key: string;
  row: GoalRow;
}

export interface DraftGroup {
  key: string;
  label: string;
  drafts: DraftRow[];
}

export const NEW_ROW_KEY_PREFIX = 'new-';

// A table row: a draft, or a Month/Week marker row (markerLabel set) that only labels what follows.
export interface TableItem extends DraftRow {
  markerLabel?: string;
  // Zebra stripe, counted over data rows only.
  isStriped?: boolean;
}

export interface DraftDiff {
  insert: GoalRow[];
  update: GoalRow[];
  delete: GoalRow[];
  // Keys of new and edited rows, for the unsaved highlight.
  changedKeys: Set<string>;
}

// A budget that fills whole weeks: one merged cell over them. `startRow` is the newest of them.
export interface ChannelBlock {
  draft: DraftRow;
  startRow: number;
  span: number;
}

// A budget that only partly covers a week (or collides with another budget): a badge in that week.
export interface ChannelBadge {
  draft: DraftRow;
  row: number;
}

export interface ChannelColumn {
  blocks: ChannelBlock[];
  badges: ChannelBadge[];
  // Keys of budgets whose dates intersect another budget in this channel.
  overlapKeys: Set<string>;
}
